import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const MetricsDisplay = () => {
  const [metrics, setMetrics] = useState({
    githubCommits: 0,
    projectsDeployed: 0,
    menteeSuccess: 0,
    codeLines: 0
  });

  const targetMetrics = {
    githubCommits: 1247,
    projectsDeployed: 89,
    menteeSuccess: 96,
    codeLines: 125000
  };

  useEffect(() => {
    const animateMetrics = () => {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setMetrics({
          githubCommits: Math.floor(targetMetrics.githubCommits * progress),
          projectsDeployed: Math.floor(targetMetrics.projectsDeployed * progress),
          menteeSuccess: Math.floor(targetMetrics.menteeSuccess * progress),
          codeLines: Math.floor(targetMetrics.codeLines * progress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setMetrics(targetMetrics);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    };

    const timer = setTimeout(animateMetrics, 1000);
    return () => clearTimeout(timer);
  }, []);

  const metricCards = [
    {
      id: 'commits',
      title: 'GitHub Commits',
      value: metrics.githubCommits.toLocaleString(),
      icon: 'GitCommit',
      color: 'brand-cyan',
      description: 'This Year'
    },
    {
      id: 'projects',
      title: 'Projects Deployed',
      value: metrics.projectsDeployed,
      icon: 'Rocket',
      color: 'brand-green',
      description: 'Live & Running'
    },
    {
      id: 'success',
      title: 'Mentee Success Rate',
      value: `${metrics.menteeSuccess}%`,
      icon: 'TrendingUp',
      color: 'brand-purple',
      description: 'Career Growth'
    },
    {
      id: 'lines',
      title: 'Lines of Code',
      value: `${(metrics.codeLines / 1000).toFixed(0)}K+`,
      icon: 'Code',
      color: 'brand-orange',
      description: 'Total Written'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-brand-cyan rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-brand-purple rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-morphism cyber-border mb-6">
            <Icon name="Activity" size={16} className="text-brand-cyan" />
            <span className="font-mono text-sm text-brand-cyan">REAL-TIME METRICS</span>
          </div>
          
          <h2 className="font-orbitron font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            System <span className="text-brand-cyan">Performance</span>
          </h2>
          
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto">
            Live data streams from the development nexus showcasing continuous innovation and impact metrics.
          </p>
        </motion.div>

        {/* Metrics Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {metricCards.map((metric) => (
            <motion.div
              key={metric.id}
              variants={cardVariants}
              className="group relative"
            >
              <div className="glass-morphism rounded-xl p-6 cyber-border hover:cyber-border-active transition-all duration-300 cyber-hover h-full">
                {/* Icon */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg bg-${metric.color}/10 flex items-center justify-center group-hover:cyber-glow transition-all duration-300`}>
                    <Icon 
                      name={metric.icon} 
                      size={24} 
                      className={`text-${metric.color} group-hover:scale-110 transition-transform duration-300`} 
                    />
                  </div>
                  
                  {/* Status Indicator */}
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 bg-${metric.color} rounded-full animate-pulse`}></div>
                    <span className="font-mono text-xs text-text-tertiary">LIVE</span>
                  </div>
                </div>

                {/* Value */}
                <div className="mb-2">
                  <div className={`font-orbitron font-bold text-2xl lg:text-3xl text-${metric.color} mb-1`}>
                    {metric.value}
                  </div>
                  <h3 className="font-inter font-medium text-sm text-foreground">
                    {metric.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="font-mono text-xs text-text-tertiary">
                  {metric.description}
                </p>

                {/* Hover Effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-transparent via-transparent to-brand-cyan/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center space-x-4 px-6 py-3 rounded-full glass-morphism cyber-border">
            <Icon name="Zap" size={16} className="text-brand-green" />
            <span className="font-mono text-sm text-text-secondary">
              Last Updated: {new Date().toLocaleTimeString()}
            </span>
            <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MetricsDisplay;